import { FC, ReactNode, useMemo } from 'react';
import { Button, Col, Divider, Popover, Row } from 'antd';
import { ListFilter } from 'lucide-react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import type { ListFilterRule } from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/01-FilterDropdown';

import { getReportFilterSummary } from './helpers';

interface DashboardFilterSummaryButtonProps {
    filters?: ListFilterRule[];
}

const DashboardFilterSummaryButton: FC<DashboardFilterSummaryButtonProps> = ({ filters = [] }): JSX.Element | null => {
    const { token } = useAntDesignTheme();
    const summary = useMemo(() => getReportFilterSummary(filters), [filters]);
    const filterCount = summary.length;

    if (!filterCount) {
        return null;
    }

    const content: ReactNode = (
        <div style={{ maxWidth: 16 * 24 }}>
            <div style={{ marginBottom: '0.5rem' }}>
                <strong>
                    {filterCount} applied {filterCount === 1 ? 'filter' : 'filters'}
                </strong>
                <br />
                <small style={{ color: token.colorTextTertiary }}>Applied through this report's filter row.</small>
            </div>
            <Divider style={{ margin: '0.5rem 0' }} />
            <div style={{ display: 'grid', gap: '0.375rem' }}>
                {summary.map((item) => (
                    <small key={item.id} style={{ color: token.colorText }}>
                        {item.fieldLabel} {item.operatorLabel.toLowerCase()} {item.valueLabel}
                    </small>
                ))}
            </div>
        </div>
    );

    return (
        <Popover content={content} placement='bottomRight' trigger='hover'>
            <Button size='small'>
                <Row align='middle' wrap={false} gutter={[6, 0]}>
                    <Col flex='none'>
                        <ListFilter
                            size='0.875rem'
                            style={{ color: token.colorTextTertiary, verticalAlign: '-0.125rem' }}
                        />
                    </Col>
                    <Col flex='none'>
                        {filterCount} {filterCount === 1 ? 'Filter' : 'Filters'}
                    </Col>
                </Row>
            </Button>
        </Popover>
    );
};

export default DashboardFilterSummaryButton;
